"use client";

import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import React, { useEffect } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

interface TourTransitionTooltipProps {
  currentTourName: string;
  nextTourName: string;
  nextTourDescription: string;
  onContinue: () => void;
  onGoBack: () => void;
  onSkip: () => void;
  isVisible: boolean;
  showBackButton?: boolean;
}

export const TourTransitionTooltip: React.FC<TourTransitionTooltipProps> = ({
  currentTourName,
  nextTourName,
  nextTourDescription,
  onContinue,
  onGoBack,
  onSkip,
  isVisible,
  showBackButton = true,
}) => {
  // Handle keyboard events
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === "ArrowRight") {
        event.preventDefault();
        onContinue();
      } else if (event.key === "Escape") {
        event.preventDefault();
        onSkip();
      } else if (event.key === "ArrowLeft" && showBackButton) {
        event.preventDefault();
        onGoBack();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onContinue, onSkip, onGoBack, showBackButton]);

  if (!isVisible) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[10001] flex items-center justify-center pointer-events-auto p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Tooltip */}
      <motion.div
        className="bg-white rounded-lg shadow-xl border border-gray-200 p-8 max-w-md w-full relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={{
          maxWidth: "min(400px, calc(100vw - 2rem))",
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4  bg-ac-3">
            🎉
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-2">
            Has completado el {currentTourName}
          </h4>
        </div>

        {/* Next tour preview */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-8 h-8 bg-ac-3 rounded-full flex items-center justify-center text-white text-sm font-semibold">
              📄
            </div>
            <h4 className="font-semibold text-gray-900">
              Siguiente: {nextTourName}
            </h4>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {nextTourDescription}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {showBackButton && (
              <Button
                variant="outline"
                size="sm"
                onClick={onGoBack}
                className="h-8"
              >
                <LuChevronLeft className="h-4 w-4 ml-1" />
                Atrás
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onSkip}
              className="h-8"
            >
              Por ahora no
            </Button>
            <Button onClick={onContinue}>
              Continuar
              <LuChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
